import React, { useState, useCallback } from 'react'
import injectStyles from 'react-jss'
import Checkbox from '@material-ui/core/Checkbox'
import CircularProgress from '@material-ui/core/CircularProgress'

import SubmitButton from '../Form/SubmitButton'
import {
  searchPostcodesPFGQL,
  searchFeaturesPFGQL,
} from '../../Module/graphQL/PathFinder/pathFinderGQL'
import { normalizeGraphQLConnection } from '../../shared/graphql/utils'
import {
  getOverlayColorAndOpacity,
  getOverlayPath,
} from '../../Containers/BuilderMap/util'
import styles from './styles'

const searchPostcodesPFGQLWrapper = async variables => {
  const response = await searchPostcodesPFGQL(variables)
  const { data, errors } = await response.json()

  if (errors && errors.length) {
    throw new Error(errors[0].message)
  }
  return normalizeGraphQLConnection(data.searchPostcodes)
}

const searchFeaturesByCodes = async ({ countryCode, layer, searchCodes }) => {
  const response = await searchFeaturesPFGQL({
    layer,
    countryCode,
    searchCodes,
    first: 500,
  })

  const { data, errors } = await response.json()

  if (errors && errors.length) {
    throw new Error(errors[0].message)
  }

  if (!data?.searchFeatures?.collection?.features) {
    return []
  }

  return data.searchFeatures.collection.features
}

const getFeatureName = (feature, countryCode) => {
  const { name, codeIRIS, statisticalSectorCode } = feature.properties

  // France uses IRIS codes
  if (countryCode === 'FR' && codeIRIS) {
    return codeIRIS
  }
  // Belgium uses statistical sector codes
  if (countryCode === 'BE' && statisticalSectorCode) {
    return statisticalSectorCode
  }
  // Default to name property
  return name
}

const formatFeaturesToOverlays = (rawFeatures, countryCode) => {
  return rawFeatures.map(feature => {
    const featureName = getFeatureName(feature, countryCode)
    const { customToggled = false } = feature.properties

    const { color, fillOpacity, strokeOpacity } = getOverlayColorAndOpacity({
      isEdit: false,
      isPostcode: true,
    })

    const paths = getOverlayPath({
      coords: feature.geometry.coordinates,
      isMultiPolygon: feature.geometry.type === 'MultiPolygon',
    })

    return {
      id: featureName,
      paths,
      color,
      name: featureName,
      metadata: feature.properties,
      geometry: feature.geometry,
      mapPolygonProps: {
        key: featureName,
        editingBlocked: true,
        canBeDeleted: false,
        fillOpacity,
        strokeOpacity,
        customToggled,
        payload: {
          label: featureName,
        },
        zIndex: 0,
      },
      isPostcode: true,
    }
  })
}

const parsePostcodeInput = input => {
  return input
    .split(/[\s,;\n]+/)
    .map(code => code.trim().toUpperCase())
    .filter(code => code.length > 0)
}

const BulkPostcodeSearch = ({
  countryCode,
  onPostcodesSelected,
  onCreateMissions,
  classes,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [selectedPostcodes, setSelectedPostcodes] = useState({})
  const [isSearching, setIsSearching] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState('')
  const [progress, setProgress] = useState({ current: 0, total: 0, text: '' })
  const [notFoundCodes, setNotFoundCodes] = useState([])

  const handleSearch = useCallback(async () => {
    const codes = parsePostcodeInput(inputValue)
    if (codes.length === 0) {
      setError('Please enter at least one postcode')
      return
    }

    setIsSearching(true)
    setError('')
    setSearchResults([])
    setSelectedPostcodes({})
    setNotFoundCodes([])

    const allResults = []
    const notFound = []
    const foundCodes = new Set()

    try {
      // For France, use searchFeatures with IRISBoundaries layer and searchCodes
      if (countryCode === 'FR') {
        const features = await searchFeaturesByCodes({
          countryCode,
          layer: 'IRISBoundaries',
          searchCodes: codes,
        })

        const postcodes = formatFeaturesToOverlays(features, countryCode)

        // Check which codes were found
        codes.forEach(code => {
          const codeUpper = code.toUpperCase()
          const found = postcodes.some(
            p =>
              p.name?.toUpperCase() === codeUpper ||
              p.metadata?.codeIRIS?.toUpperCase() === codeUpper ||
              p.metadata?.name?.toUpperCase()?.includes(codeUpper),
          )
          if (!found) {
            notFound.push(code)
          }
        })

        postcodes.forEach(p => {
          if (!foundCodes.has(p.name)) {
            allResults.push(p)
            foundCodes.add(p.name)
          }
        })
      } else {
        // For other countries, use searchPostcodes
        for (const code of codes) {
          try {
            const data = await searchPostcodesPFGQLWrapper({
              searchString: code,
              countryCode,
              first: 5,
            })

            const postcodes = formatFeaturesToOverlays(data, countryCode)

            const exactMatch = postcodes.find(
              p => p.name.toUpperCase() === code.toUpperCase(),
            )
            if (exactMatch && !foundCodes.has(exactMatch.name)) {
              allResults.push(exactMatch)
              foundCodes.add(exactMatch.name)
            } else if (postcodes.length > 0 && !exactMatch) {
              postcodes.forEach(p => {
                if (!foundCodes.has(p.name)) {
                  allResults.push(p)
                  foundCodes.add(p.name)
                }
              })
            } else if (postcodes.length === 0) {
              notFound.push(code)
            }
          } catch (err) {
            console.error(`Error searching for postcode ${code}:`, err)
            notFound.push(code)
          }
        }
      }

      setSearchResults(allResults)
      setNotFoundCodes(notFound)

      const initialSelection = {}
      allResults.forEach(p => {
        initialSelection[p.id] = true
      })
      setSelectedPostcodes(initialSelection)

      if (onPostcodesSelected) {
        onPostcodesSelected(allResults)
      }
    } catch (err) {
      setError('Failed to search postcodes. Please try again.')
      console.error(err)
    } finally {
      setIsSearching(false)
    }
  }, [inputValue, countryCode, onPostcodesSelected])

  const handleSelectAll = useCallback(
    checked => {
      const newSelection = {}
      searchResults.forEach(p => {
        newSelection[p.id] = checked
      })
      setSelectedPostcodes(newSelection)

      if (onPostcodesSelected) {
        onPostcodesSelected(checked ? searchResults : [])
      }
    },
    [searchResults, onPostcodesSelected],
  )

  const handleTogglePostcode = useCallback(
    postcodeId => {
      const newSelection = {
        ...selectedPostcodes,
        [postcodeId]: !selectedPostcodes[postcodeId],
      }
      setSelectedPostcodes(newSelection)

      if (onPostcodesSelected) {
        const selected = searchResults.filter(p => newSelection[p.id])
        onPostcodesSelected(selected)
      }
    },
    [selectedPostcodes, searchResults, onPostcodesSelected],
  )

  const handleCreateMissions = useCallback(async () => {
    const selected = searchResults.filter(p => selectedPostcodes[p.id])
    if (selected.length === 0) {
      setError('Please select at least one postcode')
      return
    }

    setIsCreating(true)
    setProgress({ current: 0, total: selected.length, text: 'Creating missions...' })

    try {
      await onCreateMissions(selected, progressData => {
        setProgress(progressData)
      })

      setSearchResults([])
      setSelectedPostcodes({})
      setInputValue('')
      setProgress({ current: 0, total: 0, text: '' })
    } catch (err) {
      setError('Failed to create missions. Please try again.')
      console.error(err)
    } finally {
      setIsCreating(false)
    }
  }, [searchResults, selectedPostcodes, onCreateMissions])

  const selectedCount = Object.values(selectedPostcodes).filter(Boolean).length
  const allSelected =
    searchResults.length > 0 && selectedCount === searchResults.length

  return (
    <div className={classes.wrapper}>
      <div className={classes.searchSection}>
        <div style={{ flex: 1 }}>
          <textarea
            className={classes.textarea}
            placeholder="Enter postcodes separated by comma, space, or new line (e.g., 2000, 2010, 2020)"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            disabled={isSearching || isCreating || disabled}
          />
          <div className={classes.helperText}>
            Enter multiple postcodes to search and create missions in bulk
          </div>
        </div>
        <SubmitButton
          className={classes.searchButton}
          value={isSearching ? 'Searching...' : 'Search'}
          onClick={handleSearch}
          disabled={isSearching || isCreating || !inputValue.trim() || disabled}
        />
      </div>

      {error && <div className={classes.errorMessage}>{error}</div>}

      {notFoundCodes.length > 0 && (
        <div className={classes.errorMessage}>
          Not found: {notFoundCodes.join(', ')}
        </div>
      )}

      {isSearching && (
        <div className={classes.loadingContainer}>
          <CircularProgress size={24} />
        </div>
      )}

      {isCreating && (
        <div className={classes.progressContainer}>
          <div className={classes.progressText}>{progress.text}</div>
          <div className={classes.progressBar}>
            <div
              className={classes.progressFill}
              style={{
                width: `${
                  progress.total > 0
                    ? (progress.current / progress.total) * 100
                    : 0
                }%`,
              }}
            />
          </div>
        </div>
      )}

      {!isSearching && !isCreating && searchResults.length > 0 && (
        <div className={classes.resultsSection}>
          <div className={classes.resultsHeader}>
            <span className={classes.resultsTitle}>
              Found {searchResults.length} postcode
              {searchResults.length !== 1 ? 's' : ''}
            </span>
            <div className={classes.selectAllContainer}>
              <Checkbox
                checked={allSelected}
                indeterminate={selectedCount > 0 && !allSelected}
                onChange={e => handleSelectAll(e.target.checked)}
                color="primary"
                size="small"
              />
              <span>Select all</span>
            </div>
          </div>

          <div className={classes.postcodeList}>
            {searchResults.map(postcode => (
              <div
                key={postcode.id}
                className={`${classes.postcodeChip} ${
                  selectedPostcodes[postcode.id]
                    ? classes.postcodeChipSelected
                    : ''
                }`}
                onClick={() => handleTogglePostcode(postcode.id)}
              >
                {postcode.name}
              </div>
            ))}
          </div>

          <div className={classes.actionsSection}>
            <span className={classes.selectedCount}>
              {selectedCount} postcode{selectedCount !== 1 ? 's' : ''} selected
            </span>
            <SubmitButton
              className={classes.createButton}
              value={`Create ${selectedCount} Mission${
                selectedCount !== 1 ? 's' : ''
              }`}
              onClick={handleCreateMissions}
              disabled={selectedCount === 0 || disabled}
            />
          </div>
        </div>
      )}

      {!isSearching &&
        !isCreating &&
        searchResults.length === 0 &&
        inputValue.trim() &&
        notFoundCodes.length > 0 && (
          <div className={classes.noResults}>
            No postcodes found. Try different search terms.
          </div>
        )}
    </div>
  )
}

export default injectStyles(styles)(BulkPostcodeSearch)
